<template>
    <div>
        <div class="container mx-auto text-center max-w-lg pb-5 scroll-to-phone">
            <div class="pb-2">
                <button class="btn" :class="textSenderButtonClass" @click.prevent="notifySender">
                    {{ $t('DELIVERY_NOTIFICATION_TEXT_ME') }}
                </button>
            </div>
            <div class="pb-3">
                <button class="btn" :class="textRecipientButtonClass" @click.prevent="notifyRecipient">
                    {{ $t('DELIVERY_NOTIFICATION_TEXT') }} {{ address.first_name }}
                </button>
            </div>
        </div>

        <div class="background--paper divider--container pt-8 pb-6">
            <div class="divider-white--top"></div>
            <div class="container mx-auto text-center max-w-lg">
                <div class="pb-3">
                    <div class="w-full max-w-xs mx-auto">
                        <telephone-field
                            v-if="shouldNotifySender"
                            v-model="senderTelephone"
                            :label="phoneNumberLabel"
                            :validation.sync="formValidations.senderTelephone"/>
                        <telephone-field
                            v-else
                            v-model="recipientTelephone"
                            :label="phoneNumberLabel"
                            :validation.sync="formValidations.recipientTelephone"/>
                    </div>
                </div>
            </div>
            <div class="divider-white--bottom"></div>
        </div>

        <div class="container mx-auto text-center max-w-lg pt-8">
            <div v-if="!hasMultipleDeliveriesSelected">
                <p>
                    {{ $t('GIFTING_ADDRESS_TABLE_PERFECT', {
                    'address.first_name': $t(`${address.first_name}`)}) }}
                    <strong>{{ getDeliveryDay }}</strong>.
                </p>
            </div>
            <div v-else>
                <p>
                    {{ $t('GIFTING_ADDRESS_TABLE_PERFECT_SO', {
                    'address.first_name': $t(`${address.first_name}`)}) }}
                    <strong>{{ firstDeliveryDate }}</strong>.
                </p>
                <p>
                    <span v-html="$t('DELIVERY_NOTIFICATION_THEN', {
                    'frequencyText': $t(`${frequencyText}`)})"/>
                    <strong v-text="durationString"></strong>
                    {{ $t('GIFTING_ADDRESS_TABLE_WE_WILL_DELIVER') }}
                </p>
                <p>
                    {{ $t('GIFTING_ADDRESS_TABLE_LAST_DELIVERY', {
                    'address.first_name': $t(`${address.first_name}`)}) }}
                    <strong>{{ lastDeliveryDate }}.</strong>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import TelephoneField from '@/components/forms/fields/LegacyTelephoneField';

export default {
    name: 'DeliveryNotification',
    props: {
        fromHolidayBoxModal: {
            default: false,
            type: Boolean,
        },
    },
    components: {
        TelephoneField,
    },
    data() {
        return {
            formValidations: {
                senderTelephone: false,
                recipientTelephone: false,
            },
            shouldNotifySender: true,
            senderTelephone: null,
            recipientTelephone: null,
        };
    },
    mounted() {
        this.senderTelephone = this.senderMobileNumber;
    },
    watch: {
        recipientTelephone(value) {
            this.notifierNumber = value;
        },
        senderTelephone(value) {
            this.notifierNumber = value;
        },
        validation(value) {
            this.$emit('update:validation', value);
        },
    },
    methods: {
        notifyRecipient() {
            this.notifier = this.address.first_name;
            this.notifierNumber = this.recipientTelephone;
            this.shouldNotifySender = false;
            this.$scrollTo('.scroll-to-phone');
        },
        notifySender() {
            this.notifier = 'You';
            this.notifierNumber = this.senderTelephone;
            this.shouldNotifySender = true;
            this.$scrollTo('.scroll-to-phone');
        },
    },
    computed: {
        ...mapGetters({
            getDeliveryForDay: 'scheduledOrder/getDeliveryForDay',
        }),
        getDeliveryDay() {
            return this.fromHolidayBoxModal ? this.getDeliveryForDay(this.firstDeliveryDate).human : this.firstDeliveryDate;
        },
        address() {
            return this.$store.getters['address/getSelectedGiftingAddress'];
        },
        deliveryDateSelected: {
            get() {
                return this.$store.getters['gifting/getDeliveryDateSelected'];
            },
            set(value) {
                this.$store.commit('gifting/setDeliveryDateSelected', value);
            },
        },
        deliveryType() {
            return this.$store.getters['gifting/getGiftType'];
        },
        durationMonths() {
            return this.$store.getters['gifting/getNumberOfMonthsForSubscription'];
        },
        durationString() {
            let monthString = this.durationMonths > 1 ? this.$t('DELIVERY_NOTIFICATION_MONTHS') : this.$t('DELIVERY_NOTIFICATION_MONTH');
            return `${this.durationMonths} ${monthString}`;
        },
        firstDeliveryDate() {
            if (this.fromHolidayBoxModal) {
                return this.$store.getters['scheduledOrder/getSelectedDelivery'].datetime;
            }
            return this.deliveryDateSelected ? formatDate(this.deliveryDateSelected, Dates.DAY_DATE_MONTH) : '';
        },
        frequencyText() {
            let frequency = this.$store.getters['gifting/getFrequencyForSubscription'];
            return this.$t(`FREQUENCY_SELECTION_${frequency.toUpperCase()}_NOUN`);
        },
        hasMultipleDeliveriesSelected() {
            return this.deliveryType === 'subscription';
        },
        lastDeliveryDate() {
            return this.$store.getters['gifting/getSubscriptionPredictedLastDeliveryDate'];
        },
        notifier: {
            get() {
                return this.$store.getters['gifting/getNotifier'];
            },
            set(value) {
                this.$store.commit('gifting/setNotifier', value);
            },
        },
        notifierNumber: {
            get() {
                return this.$store.getters['gifting/getNotifierNumber'];
            },
            set(value) {
                this.$store.commit('gifting/setNotifierNumber', value);
            },
        },
        phoneNumberLabel() {
            return this.shouldNotifySender
                ? this.$t('DELIVERY_NOTIFICATION_PLEASE')
                : this.$t('DELIVERY_NOTIFICATION_WE_WILL_NEED', { 'address.first_name': this.address.first_name });
        },
        senderMobileNumber() {
            return this.$store.getters['account/getUser']['telephone'];
        },
        textRecipientButtonClass() {
            return !this.shouldNotifySender ? 'btn--primary' : 'btn--secondary';
        },
        textSenderButtonClass() {
            return this.shouldNotifySender ? 'btn--primary' : 'btn--secondary';
        },
        validation() {
            return this.shouldNotifySender ? this.formValidations.senderTelephone : this.formValidations.recipientTelephone;
        },
    },
};
</script>
